import React from 'react';

/**
 * Dark-blue banner header (#1E3A5F, white text) over a content area, read as
 * one unit. Shared so paired columns are identical in form — same height,
 * corner radius, text size, alignment and inner padding. Uses the platform
 * card + card-header pattern; the banner colour is set inline so it is the
 * decided brand blue #1E3A5F rather than the app-wide header default.
 *
 * `h-100` lets the panel fill an equal-height Bootstrap column so two panels in
 * the same row read as a pair on desktop and laptop.
 */
export interface SectionPanelProps {
  /** Optional id on the outer card, so a link can lead straight to it. */
  id?: string;
  /** Banner heading text (already translated). */
  title: string;
  /** Optional Feather icon name (without the `feather-` prefix) shown before the title. */
  icon?: string;
  /** Optional full icon class shown before the title, e.g. a Bootstrap Icon
   * (`bi bi-graph-up-arrow`). Takes precedence over `icon` when both are set. */
  iconClassName?: string;
  /** Extra classes on the outer card (e.g. spacing overrides). */
  className?: string;
  /** Extra classes on the content area. */
  bodyClassName?: string;
  /** Optional control rendered at the banner's right edge, e.g. a collapse toggle. */
  headerAction?: React.ReactNode;
  /** Stretch to fill an equal-height column (adds `h-100`). Defaults to true for
   * the paired-column use; pass false for a standalone stacked panel. */
  fillHeight?: boolean;
  children: React.ReactNode;
}

export const SECTION_PANEL_BANNER_COLOR = '#1E3A5F';

export default function SectionPanel({ id, title, icon, iconClassName, className, bodyClassName, headerAction, fillHeight = true, children }: SectionPanelProps) {
  return (
    <div id={id} className={`card${fillHeight ? ' h-100' : ''}${className ? ` ${className}` : ''}`}>
      <div
        className={`card-header${headerAction ? ' d-flex align-items-center justify-content-between' : ''}`}
        style={{ backgroundColor: SECTION_PANEL_BANNER_COLOR }}
      >
        <h5 className="card-title section-heading mb-0">
          {iconClassName ? (
            <i className={iconClassName} aria-hidden="true"></i>
          ) : icon ? (
            <i className={`feather-${icon}`} aria-hidden="true"></i>
          ) : null}
          {title}
        </h5>
        {headerAction}
      </div>
      <div className={`card-body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>
    </div>
  );
}
